import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPermission } from '@auth';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator
} from '@base/components/ui/sidebar';
import { useBreakpoint } from '@base/hooks/use-breakpoint';
import { Avatar } from '@basic';
import { ROUTER_NAME, UIPaths } from '@consts';
import { useAccountsService, useGoalRecommendationsService } from '@hooks';
import {
  CreateIcon,
  GoalIcon,
  HomeIcon,
  LogoIcon,
  MountainWithFlagIcon,
  NotificationIcon,
  SearchMenuIcon,
  TaskIcon
} from '@icons';
import MessagesIcon from '@icons/MessagesIcon';
import { MasterName } from '@master';
import { PermissionEntityType, PermissionType } from '@models';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import CreateEntitiesMenu from './CreateEntitiesMenu';
const LayoutDesktop = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const largeScreen = useBreakpoint('2XL');
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const { isAuthenticated } = useAuth0();
  const { getGoalsRecommendationsCount } = useGoalRecommendationsService();
  const [goalsRecommendationsCount, setGoalsRecommendationsCount] = useState<number>(0);
  const messagesUnreadCount = 0;

  const isEmbedded = useMemo(() => {
    return router.query.embed?.toString().toLowerCase().trim() === 'true';
  }, [router.query.embed]);

  useEffect(() => {
    if (!isAuthenticated) {
      setGoalsRecommendationsCount(0);
      return;
    }

    if (isAuthenticated) {
      getGoalsRecommendationsCount('count')
        .then((response) => {
          setGoalsRecommendationsCount(response.count);
        })
        .catch((error) => {
          Sentry.captureException(error);
          setGoalsRecommendationsCount(0);
        });
    }
  }, [isAuthenticated]);

  const showSideBorders = useMemo(() => {
    return (
      router.pathname === ROUTER_NAME.INTRO ||
      router.pathname === ROUTER_NAME.ASSIGNMENT ||
      router.pathname === ROUTER_NAME.GOAL ||
      router.pathname === ROUTER_NAME.GIVE_ME_MORE
    );
  }, [router.pathname]);

  const { accountMe } = useAccountsService();
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState({
    show: false,
    position: { x: 0, y: 0 }
  });

  const showSideNavBar = useMemo(() => {
    if (isEmbedded) return false;
    if (
      !isAuthenticated &&
      (router.pathname === ROUTER_NAME.GOAL || router.pathname === ROUTER_NAME.GOAL_PACKAGE)
    )
      return false;
    return (
      router.pathname !== ROUTER_NAME.INTRO &&
      router.pathname !== ROUTER_NAME.VERIFY_EMAIL &&
      router.pathname !== ROUTER_NAME.APPROVALS
    );
  }, [router.pathname, isEmbedded, isAuthenticated]);

  const contentIsFullWidth = useMemo(() => {
    return (
      router.pathname === ROUTER_NAME.HOME ||
      router.pathname === ROUTER_NAME.EXPLORE ||
      router.pathname === ROUTER_NAME.MESSAGES ||
      router.pathname === ROUTER_NAME.GOAL_SUBSCRIPTIONS ||
      router.pathname === ROUTER_NAME.ASSIGNMENTS ||
      router.pathname === ROUTER_NAME.APPROVALS ||
      router.pathname === ROUTER_NAME.GOAL ||
      router.pathname === ROUTER_NAME.GOAL_PACKAGE ||
      router.pathname === ROUTER_NAME.DEV_ICONS_SINK ||
      router.pathname === ROUTER_NAME.DEV_INFO ||
      router.pathname === ROUTER_NAME.GOAL_USERS ||
      router.pathname === ROUTER_NAME.GOAL_USER ||
      router.pathname === ROUTER_NAME.GOAL_SUBSCRIPTION_VERIFICATION ||
      router.pathname === ROUTER_NAME.GOALS ||
      router.pathname === ROUTER_NAME.TASK_EDITOR ||
      router.pathname === ROUTER_NAME.NEW_STRATEGY_EDITOR ||
      router.pathname === ROUTER_NAME.GOAL_EDITOR ||
      router.pathname === ROUTER_NAME.GOAL_BASIC_EDITOR ||
      router.pathname === ROUTER_NAME.GOAL_LANDING_PAGE_EDITOR ||
      router.pathname === ROUTER_NAME.SKILL_GRAPH_EDITOR
    );
  }, [router.pathname]);

  let taskCreatePermission = useMemo(() => {
    if (!accountMe) return null;
    return getPermission(accountMe.__permissions, PermissionType.CREATE, PermissionEntityType.TASK);
  }, [accountMe]);

  let goalCreatePermission = useMemo(() => {
    if (!accountMe) return null;
    return getPermission(accountMe.__permissions, PermissionType.CREATE, PermissionEntityType.GOAL);
  }, [accountMe]);

  const onJumpToLink = (link: string) => {
    return () => {
      router.push(link);
    };
  };

  const onOpenCreateMenu = (e) => {
    setIsCreateMenuOpen({
      show: true,
      position: { x: e.clientX, y: e.clientY }
    });
  };

  const onCloseCreateMenu = () => {
    setIsCreateMenuOpen({
      show: false,
      position: { x: 0, y: 0 }
    });
  };

  let menuItems = [
    // MAIN MENU ITEMS
    {
      icon: <HomeIcon size={32} />,
      onClick: onJumpToLink(UIPaths.home()),
      active: router.pathname === ROUTER_NAME.HOME,
      label: t('components.navBar.homepage'),
      group: 'main',
      badge: null,
      dataTestId: 'navigation-menu-item-home'
    },
    {
      icon: <MountainWithFlagIcon size={32} />,
      onClick: onJumpToLink(UIPaths.goalSubscriptions()),
      active:
        router.pathname === ROUTER_NAME.GOAL_SUBSCRIPTIONS ||
        router.pathname === ROUTER_NAME.ASSIGNMENTS,
      label: t('components.navBar.subscriptions'),
      group: 'main',
      badge: null,
      dataTestId: 'navigation-menu-item-subscriptions'
    },
    {
      icon: <MessagesIcon size={22} />,
      onClick: onJumpToLink(UIPaths.messages()),
      active: router.pathname === ROUTER_NAME.MESSAGES,
      label: t('components.navBar.messages'),
      group: 'main',
      badge: messagesUnreadCount > 0 ? messagesUnreadCount : null,
      dataTestId: 'navigation-menu-item-messages'
    },
    {
      icon: <SearchMenuIcon size={32} />,
      onClick: onJumpToLink(UIPaths.explore()),
      active: router.pathname === ROUTER_NAME.EXPLORE,
      label: t('components.navBar.explore'),
      group: 'main',
      badge: goalsRecommendationsCount > 0 ? goalsRecommendationsCount : null,
      dataTestId: 'navigation-menu-item-explore'
    },
    // CREATOR MENU ITEMS
    ...[
      goalCreatePermission || taskCreatePermission
        ? {
            icon: <CreateIcon size={32} />,
            onClick: onOpenCreateMenu,
            active: isCreateMenuOpen.show,
            label: t('components.navBar.create'),
            group: 'main',
            badge: null,
            dataTestId: 'navigation-menu-item-create'
          }
        : null,
      goalCreatePermission
        ? {
            icon: <GoalIcon size={32} />,
            onClick: onJumpToLink(UIPaths.goals()),
            active: router.pathname === ROUTER_NAME.GOALS,
            label: t('components.navBar.goals'),
            group: 'creator',
            badge: null,
            dataTestId: 'navigation-menu-item-goals'
          }
        : null,
      taskCreatePermission
        ? {
            icon: <TaskIcon size={32} />,
            onClick: onJumpToLink(UIPaths.tasks()),
            active: router.pathname === ROUTER_NAME.TASKS,
            label: t('components.navBar.tasks'),
            group: 'creator',
            badge: null,
            dataTestId: 'navigation-menu-item-tasks'
          }
        : null
    ].filter((x) => x !== null),
    {
      icon: <NotificationIcon size={32} />,
      onClick: onJumpToLink(UIPaths.notifications()),
      active: router.pathname === ROUTER_NAME.NOTIFICATIONS,
      label: t('components.navBar.notifications'),
      group: 'bottom',
      badge: null,
      dataTestId: 'navigation-menu-item-notifications'
    },
    {
      icon: <Avatar src={accountMe?.user?.pictureUrl} size={32} />,
      onClick: onJumpToLink(UIPaths.accountMe()),
      active: router.pathname === ROUTER_NAME.ACCOUNT_ME,
      label: t('components.navBar.profile'),
      group: 'bottom',
      badge: null,
      dataTestId: 'navigation-menu-item-account'
    }
  ];
  const mainMenuItems = menuItems.filter((x) => x.group === 'main');
  const creatorMenuItems = menuItems.filter((x) => x.group === 'creator');
  const bottomMenuItems = menuItems.filter((x) => x.group === 'bottom');

  const renderMenuItem = (item: any, index: number, isLast: boolean) => {
    const testId =
      item.dataTestId || `navigation-menu-item-${item.label?.toLowerCase()?.replace(/\s+/g, '-')}`;

    return (
      <SidebarMenuItem
        key={index}
        className={classNames('w-full flex py-md', { 'bg-violet-700': item.active })}
      >
        <SidebarMenuButton
          className={classNames(
            'P4M',
            'w-full grow relative',
            setupForDark('!bg-transparent hover:text-primary hover:duration-300 hover:scale-110'),
            item.active ? setupForDark('!text-black') : setupForDark('text-tertiary'),
            {
              'justify-center': !sidebarExpanded
            }
          )}
          isActive={item.active}
          onClick={item.onClick}
          tooltip={item.label}
          data-testid={testId}
        >
          <div className='relative'>
            {item.icon}
            {item.badge !== null && item.badge !== undefined && (
              <div
                className={classNames(
                  'absolute flex items-center justify-center',
                  'top-[1px] left-[15px]',
                  'min-w-[13px] h-[13px]',
                  'px-[2px]',
                  'rounded-full',
                  setupForDark('bg-red-700')
                )}
              >
                <div
                  className={classNames(
                    'text-[8px] font-medium leading-[1.3] text-center tracking-[0.16px]',
                    setupForDark('text-white')
                  )}
                >
                  {item.badge}
                </div>
              </div>
            )}
          </div>
          <span className={sidebarExpanded ? '' : 'hidden'}>{item.label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };
  return (
    <div className='h-dvh w-full overflow-hidden hidden sm:block'>
      <SidebarProvider
        defaultOpen={largeScreen === true}
        onOpenChange={(isOpen) => setSidebarExpanded(isOpen)}
        open={sidebarExpanded}
        className={classNames('m-0 p-0')}
      >
        <div
          className={classNames('fixed left-0 top-0 z-30 h-full', showSideNavBar ? '' : 'hidden')}
        >
          <Sidebar
            variant='floating'
            className={classNames(
              'm-0 p-0',
              '!border-0',
              '!border-r',
              'w-[300px]',
              'border-r-[1px]',
              setupForDark('!border-primary !bg-nav')
            )}
            collapsible={'icon'}
          >
            <SidebarHeader
              className={classNames(sidebarExpanded ? 'px-xxl py-xxxl' : 'px-sm py-xxl')}
            >
              <div className='flex flex-row justify-between items-center'>
                <div
                  className='flex items-center gap-[13px] cursor-pointer'
                  onClick={onJumpToLink(UIPaths.home())}
                >
                  <div className='w-[42px] h-[42px]'>
                    <LogoIcon size={42} backgroundColor='transparent' animateOnHover />
                  </div>
                  <div
                    className={classNames(
                      setupForDark('text-primary h-[32px] pt-[2px]'),
                      sidebarExpanded ? '' : 'hidden'
                    )}
                  >
                    <MasterName forceHeight={32} />
                  </div>
                </div>
              </div>
            </SidebarHeader>

            <SidebarContent className={classNames('pt-xxl', sidebarExpanded ? 'px-xxl' : 'px-x')}>
              <SidebarMenu>
                {mainMenuItems.map((item, index) =>
                  renderMenuItem(item, index, index === mainMenuItems.length - 1)
                )}
              </SidebarMenu>

              {creatorMenuItems.length > 0 && (
                <>
                  <SidebarSeparator
                    className={classNames(setupForDark('border-secondary'), 'my-xxl')}
                  />
                  <SidebarMenu>
                    {creatorMenuItems.map((item, index) =>
                      renderMenuItem(item, index, index === creatorMenuItems.length - 1)
                    )}
                  </SidebarMenu>
                </>
              )}

              <SidebarSeparator
                className={classNames(setupForDark('border-secondary'), 'my-xxl')}
              />
              <SidebarMenu className='justify-end h-full'>
                {bottomMenuItems.map((item, index) =>
                  renderMenuItem(item, index, index === bottomMenuItems.length - 1)
                )}
              </SidebarMenu>
            </SidebarContent>
          </Sidebar>
        </div>
        {/* Scroll container spans the full available width so the scrollbar sits at the
            viewport's right edge; the readable content column is centered inside it. */}
        <div
          className={classNames(
            'h-dvh overflow-y-auto',
            showSideNavBar ? 'w-[calc(100%-65px)] ml-[65px]' : 'w-full',
            // FIXME: https://app.clickup.com/t/8699jucr0
            router.pathname === ROUTER_NAME.ASSIGNMENT ? 'mr-[49px]' : '' // Fix for the sidebar covering the content and the X
          )}
        >
          <div
            className={classNames(
              // Full-width pages fill the scroll container (h-full) so their h-full children keep working;
              // column/flow pages grow with content (min-h-full) so the scrollbar sits at the viewport's right edge.
              contentIsFullWidth ? 'h-full' : 'min-h-full max-w-[1150px] xl:mx-auto',
              showSideBorders ? 'xl:border-x-[1px] xl:border-primary' : ''
            )}
          >
            {children}
          </div>
        </div>
      </SidebarProvider>
      {isCreateMenuOpen.show && (
        <CreateEntitiesMenu
          isOpen={isCreateMenuOpen.show}
          position={isCreateMenuOpen.position}
          onClose={onCloseCreateMenu}
        />
      )}
    </div>
  );
};

export default LayoutDesktop;
